import { PageAnchorTags, PageHeader, Separator } from './AccountSectionWrapper'
import { useFormInput } from '@/components/hooks/useFormInput'
import { accountTranslations } from '../helpers/translations'
import {
  AccountKidProfileProfileDetailsSelector,
  AccountKidProfileAvatarSelector,
  PROFILE_ICONS
} from './AccountKidProfile'
import { useAccountLayoutContext } from './AccountProfileContext'
import { getProfileIcon } from '../helpers/getProfileIcon'
import { birthdayStringToOmneoObject, parseBirthday } from '../helpers/parseBirthday'
import { useState } from 'preact/hooks'
import { AccountKidsProfileBrand } from './AccountKidsProfileBrand'
import { AccountKidsProfileSports } from './AccountKidsProfileSports'
import { AccountMeasurements } from './AccountMeasurements'
import { AccountShoeWardrobe } from './AccountShoeWardrobe'
import { useRefetch } from '../hooks/useRefetch'
import { AccountKidsProfileWishlist } from './AccountKidsProfileWishlist'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'

const validationSubmitKeys = ['firstName', 'lastName', 'birthday', 'gender', 'shoeTag', 'avatar']
export const AccountKidProfileNew = ({ handleBack }) => {
  const { omneoClient, cacheConfig } = useAccountLayoutContext()
  const { handleRefetch } = useRefetch()
  const [errors, setErrors] = useState({})
  const [config, setConfig] = useState({
    connectionId: null,
    profile: null
  })
  const [isLoading, setIsLoading] = useState(false)

  const { formInputs, handleOnChange } = useFormInput({
    firstName: '',
    lastName: '',
    birthday: '',
    gender: 'withheld',
    shoeTag: null,
    avatar: '0'
  })

  const resetErrors = () => {
    setErrors({})
  }

  const isValidProfile = config.connectionId && config.profile
  const pageTitle = config?.connectionId ? `${config?.profile?.first_name}'s profile` : accountTranslations.kids_profiles.new_profile.title
  const handleNewProfileSubmitAsync = async (e) => {
    e.preventDefault()

    resetErrors()
    setIsLoading(true)
    const valid = validationSubmitKeys.some((key) => !!formInputs[key])
    if (!valid) {
      setIsLoading(false)
      return
    }

    const { birth_month: birthMonth, birth_year: birthYear } = formInputs.birthday.value
      ? birthdayStringToOmneoObject(formInputs.birthday.value)
      : { birth_month: '', birth_year: '' }
    const payload = {
      firstName: formInputs.firstName.value,
      lastName: formInputs.lastName.value,
      gender: formInputs.gender.value,
      shoeTag: formInputs.shoeTag.value,
      avatarUrl: getProfileIcon(formInputs.avatar.value, PROFILE_ICONS),
      birthMonth,
      birthYear
    }

    if (config.connectionId) {
      // Handle the update functions
      return
    }
    let createResponse

    try {
      createResponse = await omneoClient.profileClient.createDependantProfileConnection(payload)
    } catch (e) {
      console.error('Error creating child profile', e)
      setErrors({
        shoeTag: 'duplicate'
      })
      setIsLoading(false)
      return
    }

    const connection = omneoClient.client.profile.Connection(createResponse.id)
    const profile = await connection.get()

    // Create wishlist for the child
    const wishlist = await omneoClient.getOrCreateList(undefined, 'wishlist')
    profile.wishlist = wishlist

    /**
     * update the config to set the connectionId so we can refetch the
     */
    setConfig((c) => ({
      ...c,
      connectionId: createResponse.id,
      profile
    }))
    cacheConfig.updateCacheConfig('ProfileConnections', null)
    handleRefetch()
    setIsLoading(false)
  }

  const handleSuspenseAsync = async () => {
    if (config.connectionId) {
      const connection = omneoClient.client.profile.Connection(config.connectionId)
      const profile = await connection.get()
      setConfig((c) => ({
        ...c,
        profile
      }))
    }
  }

  return (
      <div className={'flex flex-col gap-4'}>
        <PageHeader title={pageTitle} />
        {isValidProfile && <PageAnchorTags />}
        <form onSubmit={handleNewProfileSubmitAsync} className={'space-y-4'}>
          <AccountKidProfileAvatarSelector
            formInputs={formInputs}
            handleOnChange={handleOnChange}
            newProfile={!isValidProfile}
            errors={errors}
          />
          <Separator />
          <AccountKidProfileProfileDetailsSelector
            formInputs={formInputs}
            handleOnChange={handleOnChange}
            newProfile={!isValidProfile}
          />
          <div className={'flex flex-row gap-2'}>
            <button type={'button'} className={'btn btn-outline-black btn-md'} onClick={handleBack}>
              Cancel
            </button>
            <button type={'submit'} className={'btn btn-solid-black btn-md relative'} disabled={isLoading}>
              {isLoading && <LoaderSpin />}
              <span className={`${isLoading ? 'invisible' : ''}`}>Save</span>
            </button>
          </div>
        </form>
        <Separator />
        {isValidProfile && (
          <>
            <AccountShoeWardrobe />
            <AccountMeasurements
              props={{
                connectionId: config.connectionId
              }}
            />
            <AccountKidsProfileBrand
              profile={config.profile}
              connectionId={config.connectionId}
              handleRefetch={handleSuspenseAsync}
            />
            <AccountKidsProfileSports
              profile={config.profile}
              connectionId={config.connectionId}
              handleRefetch={handleSuspenseAsync}
            />
            <AccountKidsProfileWishlist
              profile={config.profile}
              connectionId={config.connectionId}
              handleRefetch={handleSuspenseAsync}
            />
          </>
        )}
      </div>
  )
}
